import { useTranslation } from "react-i18next"

import type { CalculatedKPIs } from "../../types/use-case.types"

import { cn } from "@/lib/utils"

import { TENK_COLORS } from "../../types/use-case.types"
import { formatHours, formatNumber, formatUSD } from "../../utils/format-helpers"

type FactSheetKPIsProps = {
  kpis: CalculatedKPIs
  investment: string
}

function KpiCard({
  title,
  value,
  subtitle,
  className,
}: {
  title: string
  value: string
  subtitle: string
  className?: string
}) {
  return (
    <div className={cn("rounded-lg border p-3 shadow-sm", className)}>
      <h4 className="text-muted-foreground text-[10px] font-bold tracking-wide uppercase">
        {title}
      </h4>
      <div className="mt-1 text-lg font-bold">{value}</div>
      <div className="text-muted-foreground text-[10px]">{subtitle}</div>
    </div>
  )
}

export function FactSheetKPIs({ kpis, investment }: FactSheetKPIsProps) {
  const { t } = useTranslation()

  const tenkColor = TENK_COLORS[kpis.tenkCategory]
  const tenkBgMap: Record<string, string> = {
    Realistic: "bg-success/10 border-success/30",
    Ambitious: "bg-warning/10 border-warning/30",
    Minimal: "bg-muted/50 border-border",
    "Low success rate": "bg-destructive/15 border-destructive/40",
  }

  return (
    <div>
      <div className="grid grid-cols-4 gap-2">
        <KpiCard
          subtitle={t("useCases.factSheet.kpis.perEmployee")}
          title={t("useCases.factSheet.kpis.hoursSavedYear")}
          value={formatHours(kpis.annualHoursPerPerson) + " h"}
        />
        <KpiCard
          subtitle={t("useCases.factSheet.kpis.initialInvestment")}
          title={t("useCases.factSheet.kpis.oneTimeCost")}
          value={formatUSD(parseFloat(investment) || 0)}
        />
        <KpiCard
          subtitle={t("useCases.factSheet.kpis.acrossAllUsers")}
          title={t("useCases.factSheet.kpis.totalCapacity")}
          value={formatNumber(kpis.totalCapacityHours) + " h"}
        />
        <KpiCard
          subtitle={t("useCases.factSheet.kpis.recurringOnly")}
          title={t("useCases.factSheet.kpis.yearlyOpCost")}
          value={formatUSD(kpis.opCostYearly)}
        />
        <KpiCard
          subtitle={t("useCases.factSheet.kpis.fullTimeFreed")}
          title={t("useCases.factSheet.kpis.fteEquivalent")}
          value={formatHours(kpis.fteEquivalent) + " FTE"}
        />
        <KpiCard
          subtitle={t("useCases.factSheet.kpis.directSavings")}
          title={t("useCases.factSheet.kpis.hardSavings")}
          value={formatUSD(kpis.hardSavings)}
        />
        <KpiCard
          subtitle={t("useCases.factSheet.kpis.timeValueFreed")}
          title={t("useCases.factSheet.kpis.softSavings")}
          value={formatUSD(kpis.softSavings)}
        />
        <KpiCard
          subtitle={t("useCases.factSheet.kpis.softPlusHard")}
          title={t("useCases.factSheet.kpis.annualBenefit")}
          value={formatUSD(kpis.annualBenefit)}
        />
      </div>

      <div className="mt-2 grid grid-cols-2 gap-2">
        {/* $10K Rule */}
        <div className={cn("rounded-lg border p-3 shadow-sm", tenkBgMap[kpis.tenkCategory])}>
          <h4 className="text-muted-foreground text-[10px] font-bold tracking-wide uppercase">
            {t("useCases.factSheet.kpis.tenkRule")}
          </h4>
          <div className="mt-1 text-lg font-bold" style={{ color: tenkColor }}>
            {formatUSD(Math.round(kpis.tenkQuarterly))}/quarter
          </div>
          <div className="text-xs font-bold" style={{ color: tenkColor }}>
            {kpis.tenkCategory}
          </div>
          <div className="text-muted-foreground text-[10px]">
            {t("useCases.factSheet.kpis.annual")}: {formatUSD(kpis.annualBenefit)}
          </div>
        </div>

        {/* ROI 12m */}
        <div
          className={cn(
            "rounded-lg border p-3 shadow-sm",
            kpis.roi12m >= 0
              ? "border-success/30 bg-success/10"
              : "border-destructive/30 bg-destructive/10"
          )}
        >
          <h4 className="text-muted-foreground text-[10px] font-bold tracking-wide uppercase">
            {t("useCases.factSheet.kpis.roi12m")}
          </h4>
          <div
            className={cn(
              "mt-1 text-lg font-bold",
              kpis.roi12m >= 0 ? "text-success" : "text-destructive"
            )}
          >
            {formatUSD(kpis.roi12m)}
          </div>
          <div className="text-muted-foreground text-[10px]">
            {t("useCases.factSheet.kpis.benefitMinusCosts")}
          </div>
        </div>
      </div>
    </div>
  )
}
